import { Button, Stack, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { useMemo, useState } from "react";

import AppIcon from "@/common/components/Icons/AppIcon";
import { AutocompleteOption } from "@/common/ts/autocomplete";
import { RepairSpecDto } from "@/core/api/generated/v1";

import RepairSpecCreateUpdate, {
  RepairSpecCreateUpdateProps,
} from "./CreateUpdate/RepairSpecCreateUpdate";
import RepairSpecCreateUpdateModal from "./CreateUpdate/RepairSpecCreateUpdateModal";
import RepairSpecAutocomplete, { RepairSpecAutocompleteProps } from "./RepairSpecAutocomplete";

interface OwnProps {
  autocompleteProps: RepairSpecAutocompleteProps;
  createUpdateProps?: Pick<RepairSpecCreateUpdateProps, "defaultValues">;
  createFormPlacement?: "modal" | "page";
  onCreateStart?: () => void;
  onCreate: (newValue: RepairSpecDto) => void;
}

type Props = OwnProps;

export default function SelectOrCreateRepairSpec({
  autocompleteProps,
  createUpdateProps,
  createFormPlacement = "modal",
  onCreateStart,
  onCreate,
}: Props) {
  const [isCreate, setIsCreate] = useState(false);
  const [defaultValues, setDefaultValues] = useState<
    RepairSpecCreateUpdateProps["defaultValues"] | undefined
  >(undefined);

  const _onCreate = useMemo(() => {
    return (newOption: AutocompleteOption<RepairSpecDto> | undefined) => {
      setDefaultValues({
        ...createUpdateProps?.defaultValues,
        name: newOption?.inputValue || undefined,
      });
      setIsCreate(true);
      onCreateStart && onCreateStart();
    };
  }, [onCreateStart, createUpdateProps?.defaultValues]);

  return (
    <Stack spacing={2}>
      {/* 1. Search repairSpec */}
      {!isCreate && (
        <Box>
          <RepairSpecAutocomplete {...autocompleteProps} withCreate onCreate={_onCreate} />
        </Box>
      )}

      {isCreate && (
        <Box>
          {isCreate && (
            <>
              {createFormPlacement === "modal" && (
                <RepairSpecCreateUpdateModal
                  open={isCreate}
                  onClose={() => setIsCreate(false)}
                  createUpdateProps={{
                    defaultValues: defaultValues,
                    onCreate: onCreate,
                    onSave: (newValue) => {
                      setIsCreate(false);
                    },
                  }}
                />
              )}

              {createFormPlacement === "page" && (
                <Stack spacing={1}>
                  <Stack direction='row' spacing={1} sx={{ alignItems: "center" }}>
                    <Button
                      variant='text'
                      color='text'
                      size='small'
                      startIcon={<AppIcon of='goBack' />}
                      onClick={() => setIsCreate(false)}
                    >
                      Back to search
                    </Button>

                    <Typography component='div' variant='subtitle1'>
                      Create new repair spec
                    </Typography>
                  </Stack>

                  <RepairSpecCreateUpdate
                    defaultValues={defaultValues}
                    onCreate={onCreate}
                    onSave={(newValue) => {
                      setIsCreate(false);
                    }}
                  />
                </Stack>
              )}
            </>
          )}
        </Box>
      )}
    </Stack>
  );
}
